import type { AppShellProps } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import type { CSSProperties } from "react";
import { useSnapshot } from "valtio";
import { assistantStore } from "@/store/assistant";
import {
	ASSISTANT_HEADER_HEIGHT,
	ASSISTANT_PANEL_WIDTH,
} from "./assistant-panel.styles";

/** Breakpoint md Mantine (= `breakpoint: "md"` di aside AppShell): di atasnya panel membagi layar dengan halaman. */
export const ASSISTANT_DESKTOP_QUERY = "(min-width: 62em)";

export interface AssistantLayout {
	/** Lebar kolom kanan milik panel (0 = tertutup/diperbesar). */
	asideWidth: number;
	/** Jarak (px) header berhenti dari tepi kanan layar: header memendek sampai tepi kiri panel. */
	headerInsetEnd: number;
	/** Turunan panel dari atas layar (px): 0 = panel setinggi layar. */
	panelTopOffset: number;
}

/**
 * Tata letak halaman-vs-panel (murni). Desktop mode normal: panel setinggi
 * layar dan header memendek sebesar lebar panel. Mode diperbesar & di bawah
 * md panel menutupi halaman, jadi header tetap penuh dan panel dimulai di
 * bawah header (bila halaman punya header).
 */
export function assistantLayoutFor(input: {
	open: boolean;
	maximized: boolean;
	desktop: boolean;
	withHeader: boolean;
}): AssistantLayout {
	const asideWidth = assistantSpaceFor(input.open, input.maximized);
	const overlay = input.maximized || !input.desktop;
	return {
		asideWidth,
		headerInsetEnd: input.desktop ? asideWidth : 0,
		panelTopOffset: input.withHeader && overlay ? ASSISTANT_HEADER_HEIGHT : 0,
	};
}

/** Layout panel untuk komponen: membaca store + lebar layar, lalu memakai `assistantLayoutFor`. */
export function useAssistantLayout(withHeader: boolean): AssistantLayout {
	const { open, maximized } = useSnapshot(assistantStore);
	const desktop = useMediaQuery(ASSISTANT_DESKTOP_QUERY, false, {
		getInitialValueInEffect: false,
	});
	return assistantLayoutFor({ open, maximized, desktop, withHeader });
}

/**
 * Gaya header AppShell agar berhenti di tepi kiri panel. Margin ikut
 * `transition-duration` AppShell (0 bila animasi mati / reduced-motion).
 */
export function assistantHeaderStyle(insetEnd: number): CSSProperties {
	return { marginInlineEnd: insetEnd };
}

/** Lebar kolom kanan yang dipakai panel: hanya saat terbuka dan mode normal (perbesar menutupi halaman). */
export function assistantSpaceFor(open: boolean, maximized: boolean): number {
	return open && !maximized ? ASSISTANT_PANEL_WIDTH : 0;
}

/** Ruang (px) yang harus disisakan halaman di kanan; satu sumber untuk MainLayout, /profile, dan /wall. */
export function useAssistantSpace(): number {
	const { open, maximized } = useSnapshot(assistantStore);
	return assistantSpaceFor(open, maximized);
}

/**
 * Konfigurasi `aside` AppShell untuk ruang panel: Mantine menggeser konten
 * (dengan transisi); elemen Aside tidak dirender (panel adalah Drawer
 * setinggi layar, header dipendekkan lewat `assistantHeaderStyle`). Di bawah
 * breakpoint md panel tetap melayang menutupi halaman.
 */
export function assistantAsideConfig(space: number): AppShellProps["aside"] {
	return {
		width: ASSISTANT_PANEL_WIDTH,
		breakpoint: "md",
		collapsed: { desktop: space === 0, mobile: true },
	};
}
